<template>
  <div class="history-page" :class="{ editing: isEditing }">
    <div class="bg-decoration">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
    </div>

    <div class="content-wrapper">
      <aside class="sidebar">
        <nav class="nav-card">
          <div class="nav-logo">
            <div class="logo-icon">
              <img :src="logo2Image" alt="" class="logo2-icon" />
            </div>
            <div class="brand-copy">
              <h1 class="brand-name">树苗英语</h1>
              <span class="brand-subtitle">TREE SAPLING</span>
            </div>
          </div>

          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.id">
              <a
                href="#"
                @click.prevent="handleNavItemClick(item)"
                :class="['nav-item', { active: activeNav === item.id }]"
              >
                <font-awesome-icon :icon="item.icon" class="nav-icon" />
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
          <div class="nav-menu-visual" aria-hidden="true">
            <img :src="menuBgImage" alt="" class="nav-menu-image" />
          </div>
        </nav>
      </aside>

      <main ref="historyShellRef" class="history-shell">
        <header class="history-tabs" aria-label="内容分类">
          <button type="button" class="mobile-back-btn" @click="handleGoBack" aria-label="返回上一页">
            <font-awesome-icon icon="chevron-left" />
          </button>

          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="history-tab"
            :class="{ active: activeTab === tab.id }"
            @click="selectTab(tab.id)"
          >
            {{ tab.label }}
          </button>
        </header>

        <section class="history-toolbar">
          <div class="type-tabs" aria-label="内容类型">
            <button
              v-for="type in typeOptions"
              :key="type.id"
              type="button"
              class="type-tab"
              :class="{ active: activeType === type.id }"
              @click="selectType(type.id)"
            >
              {{ type.label }}
            </button>
          </div>

          <button type="button" class="edit-btn" :class="{ active: isEditing }" @click="toggleEditing">
            <font-awesome-icon :icon="isEditing ? 'xmark' : 'pen-to-square'" />
            <span>{{ isEditing ? '取消' : '编辑' }}</span>
          </button>
        </section>

        <section class="content-grid" :class="`type-${activeType}`" :aria-label="currentTabLabel">
          <template v-if="showVideoSkeleton">
            <div
              v-for="index in queryParams.pageSize"
              :key="`video-skeleton-${index}`"
              class="content-card skeleton-card"
            >
              <el-skeleton animated>
                <template #template>
                  <el-skeleton-item variant="image" class="skeleton-cover" />
                  <el-skeleton-item variant="text" class="skeleton-title" />
                </template>
              </el-skeleton>
            </div>
          </template>

          <template v-else>
            <article
              v-for="item in visibleItems"
              :key="item.id"
              class="content-card"
              :class="[
                `content-card-${item.type}`,
                {
                  selected: selectedIds.includes(item.id),
                  editing: isEditing,
                  playing: currentAudioItemId === item.id || currentTprItemId === item.playId
                }
              ]"
              @click="handleCardClick(item)"
            >
              <template v-if="item.type === 'audio'">
                <div class="audio-history-icon">
                  <font-awesome-icon :icon="currentAudioItemId === item.id ? 'volume-high' : 'headphones'" />
                </div>
                <div class="audio-history-copy">
                  <h2 class="audio-history-title">{{ item.title }}</h2>
                  <p class="audio-history-date">{{ item.date }}</p>
                </div>
                <span v-if="isEditing" class="select-mark audio-select-mark">
                  <font-awesome-icon v-if="selectedIds.includes(item.id)" icon="check" />
                </span>
              </template>
              <template v-else-if="item.type === 'card'">
                <div
                  class="tpr-history-surface"
                  :aria-label="`播放 ${item.title}`"
                  role="button"
                  tabindex="0"
                  @click.stop="handleCardClick(item)"
                  @keydown.enter.prevent="handleCardClick(item)"
                  @keydown.space.prevent="handleCardClick(item)"
                >
                  <div class="tpr-history-video-wrap" :style="{ '--cover-start': item.coverStart, '--cover-end': item.coverEnd }">
                    <video
                      :ref="(el) => setTprVideoRef(item.playId, el)"
                      class="tpr-history-video"
                      :class="{ active: currentTprItemId === item.playId }"
                      :src="item.videoUrl"
                      :poster="item.coverUrl"
                      playsinline
                      preload="auto"
                      controlslist="nodownload nofullscreen noremoteplayback"
                      disablepictureinpicture
                      @click.stop="handleTprVideoElementClick(item)"
                      @play="handleTprVideoPlay(item.playId)"
                      @pause="handleTprVideoPause(item.playId)"
                      @ended="handleTprVideoEnded(item.playId)"
                    ></video>

                    <div v-if="currentTprItemId !== item.playId && item.videoUrl" class="tpr-history-video-status" aria-hidden="true">
                      <font-awesome-icon icon="play" />
                    </div>

                    <span v-if="isEditing" class="select-mark">
                      <font-awesome-icon v-if="selectedIds.includes(item.id)" icon="check" />
                    </span>
                  </div>
                </div>
                <h2 class="content-title">{{ item.title }}</h2>
              </template>
              <template v-else>
                <div
                  class="cover-box"
                  :class="{ 'has-cover': item.coverUrl }"
                  :style="{ '--cover-start': item.coverStart, '--cover-end': item.coverEnd }"
                >
                  <img v-if="item.coverUrl" :src="item.coverUrl" alt="" class="cover-image" />
                  <font-awesome-icon v-else icon="image" class="cover-icon" />
                  <span v-if="isEditing" class="select-mark">
                    <font-awesome-icon v-if="selectedIds.includes(item.id)" icon="check" />
                  </span>
                </div>
                <h2 class="content-title">{{ item.title }}</h2>
              </template>
            </article>
          </template>

          <div v-if="!showVideoSkeleton && visibleItems.length === 0" class="empty-state">
            <font-awesome-icon :icon="activeTab === 'history' ? 'clock-rotate-left' : 'heart'" />
            <p>{{ activeTab === 'history' ? '暂无观看历史' : '暂无收藏内容' }}</p>
          </div>

          <div v-if="isVideoPageLoading" class="favorite-loading-state">
            <el-skeleton animated>
              <template #template>
                <el-skeleton-item variant="text" class="favorite-loading-line" />
              </template>
            </el-skeleton>
          </div>
        </section>
      </main>
    </div>

    <Transition name="batch-bar">
      <footer v-if="isEditing" class="batch-actions">
        <button type="button" class="batch-btn select-all-btn" @click="selectAllVisibleItems">
          全选
        </button>
        <button
          type="button"
          class="batch-btn delete-btn"
          :class="{ active: hasSelectedItems }"
          :disabled="!hasSelectedItems"
          @click="deleteSelectedItems"
        >
          删除
        </button>
      </footer>
    </Transition>

    <Transition name="player-dock">
      <section v-if="currentAudioItem" class="history-audio-player-dock" aria-label="音频播放器">
        <div class="history-audio-player-copy">
          <span class="history-audio-player-label">正在播放</span>
          <strong class="history-audio-player-title">{{ currentAudioItem.title }}</strong>
        </div>
        <audio
          ref="audioPlayerRef"
          class="history-audio-player"
          :src="currentAudioSrc"
          controls
          preload="metadata"
          @ended="handleAudioEnded"
        ></audio>
      </section>
    </Transition>

    <ParentPasswordModal ref="parentPasswordModalRef" />
  </div>
</template>

<script setup lang="ts">
import logo2Image from '../assets/logo2.png'
import menuBgImage from '../assets/menu_bg.jpg'
import ParentPasswordModal from '../components/ParentPasswordModal.vue'
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getFavoritesPage, batchDeleteFavorites, type FavoritesVO } from '@/api/favorites'
import { deleteHistory, getHistoryPage, type HistoryVO } from '@/api/history'
import { getTprList, type TprVO } from '@/api/course-tpr'

const route = useRoute()
const router = useRouter()
const AUTH_STORAGE_KEY = 'tree-sprout-auth'
const PARENT_VERIFIED_STORAGE_KEY = 'tree-sprout-parent-verified'

const tabs = [
  { id: 'history', label: '历史' },
  { id: 'favorites', label: '收藏' },
]

const typeOptions = [
  { id: 'video', label: '视频' },
  { id: 'audio', label: '音频' },
  { id: 'card', label: 'TPR' },
  { id: 'word', label: '单词' },
]

const queryParams = ref({
  pageNo: 1,
  pageSize: 12,
  contentType: 1
})

const favoritesList = ref<FavoritesVO[]>([])
const historyList = ref<HistoryVO[]>([])
const dataTotal = ref(0)
const isLoading = ref(false)
const tprDetailMap = ref<Record<string, TprVO>>({})
const loadedTprDetailKeys = new Set<string>()

const getFavoriteVideoItemId = (item: FavoritesVO) => item.id
const getHistoryVideoItemId = (item: HistoryVO) => item.id

const getContentTypeByType = (typeId) => {
  if (typeId === 'audio') {
    return 2
  }

  if (typeId === 'card') {
    return 3
  }

  if (typeId === 'word') {
    return 4
  }

  return 1
}

const syncQueryContentType = () => {
  queryParams.value.contentType = getContentTypeByType(activeType.value)
}

type ApiListItem = HistoryVO | FavoritesVO | Record<string, any>

const normalizeId = (id) => id === undefined || id === null ? '' : String(id)

const getApiItemContent = (item: ApiListItem) => {
  return item.content ||
    item.contentInfo ||
    item.detail ||
    item.wordSeries ||
    item.wordSeriesVO ||
    item.courseWordSeries ||
    item.courseWordSeriesVO ||
    item.tpr ||
    item.courseTpr ||
    item.courseTprDO ||
    item.courseTprVO ||
    item.resource ||
    {}
}

const getApiItemCachedTpr = (item: ApiListItem) => {
  const content = getApiItemContent(item)
  const contentId = normalizeId(item.contentId ?? content.id)
  return contentId ? tprDetailMap.value[contentId] || null : null
}

const getApiItemSeriesId = (item: ApiListItem) => {
  const content = getApiItemContent(item)
  return normalizeId(item.seriesId || item.contentId || content.seriesId || content.id)
}

const getApiItemField = (item: ApiListItem, key: string) => {
  const content = getApiItemContent(item)
  const cachedTpr = getApiItemCachedTpr(item)
  return item[key] || content[key] || cachedTpr?.[key] || ''
}

const getApiItemTitle = (item: HistoryVO | FavoritesVO) => {
  if (activeType.value === 'card') {
    const english = getApiItemField(item, 'english')
    const chinese = getApiItemField(item, 'chinese')
    const name = getApiItemField(item, 'name')
    return [english, chinese].filter(Boolean).join(' / ') || name || 'TPR'
  }

  if (activeType.value === 'word') {
    return getApiItemField(item, 'name') ||
      getApiItemField(item, 'title') ||
      getApiItemField(item, 'seriesName') ||
      getApiItemField(item, 'wordSeriesName') ||
      '单词闪卡'
  }

  return getApiItemField(item, 'name') || getApiItemField(item, 'english') || getApiItemField(item, 'chinese') || ''
}

const getApiItemVideoUrl = (item: HistoryVO | FavoritesVO) => getApiItemField(item, 'videoUrl')
const getApiItemCoverUrl = (item: HistoryVO | FavoritesVO) => getApiItemField(item, 'coverUrl')
const getApiItemPlayId = (item: HistoryVO | FavoritesVO) => normalizeId(item.contentId || getApiItemContent(item).id || item.id)

const favoriteVideoItems = computed(() => {
  return favoritesList.value.map((item) => ({
    id: getFavoriteVideoItemId(item),
    playId: getApiItemPlayId(item),
    tab: 'favorites',
    type: activeType.value,
    title: getApiItemTitle(item),
    contentId: item.contentId,
    seriesId: item.seriesId || item.contentId,
    seasonId: item.seasonId,
    category: getApiItemField(item, 'category'),
    chinese: getApiItemField(item, 'chinese'),
    english: getApiItemField(item, 'english'),
    date: item.createTime,
    audioUrl: getApiItemField(item, 'audioUrl'),
    videoUrl: getApiItemVideoUrl(item),
    coverUrl: getApiItemCoverUrl(item),
    coverStart: '#e8e8e8',
    coverEnd: '#d9d9d9',
  }))
})

const historyVideoItems = computed(() => {
  return historyList.value.map((item) => ({
    id: getHistoryVideoItemId(item),
    playId: getApiItemPlayId(item),
    tab: 'history',
    type: activeType.value,
    title: getApiItemTitle(item),
    contentId: item.contentId,
    seriesId: item.seriesId || item.contentId,
    seasonId: item.seasonId,
    category: getApiItemField(item, 'category'),
    chinese: getApiItemField(item, 'chinese'),
    english: getApiItemField(item, 'english'),
    date: item.createTime,
    audioUrl: getApiItemField(item, 'audioUrl'),
    videoUrl: getApiItemVideoUrl(item),
    coverUrl: getApiItemCoverUrl(item),
    coverStart: '#e8e8e8',
    coverEnd: '#d9d9d9',
  }))
})

const contentItems = ref([
  {
    id: 'history-video-1',
    tab: 'history',
    type: 'video',
    title: 'yakka dee 第一季 01 Banana',
    season: 's1',
    episode: 's1-1',
    coverStart: '#e8e8e8',
    coverEnd: '#d9d9d9',
  },
  {
    id: 'history-video-2',
    tab: 'history',
    type: 'video',
    title: 'yakka dee 第二季 01 Banana',
    season: 's2',
    episode: 's2-1',
    coverStart: '#e5e5e5',
    coverEnd: '#d7d7d7',
  },
  {
    id: 'history-video-3',
    tab: 'history',
    type: 'video',
    title: 'SSS-唱跳儿歌(TPR)',
    coverStart: '#e7e7e7',
    coverEnd: '#d8d8d8',
  },
  {
    id: 'history-video-4',
    tab: 'history',
    type: 'video',
    title: 'Super Simple Songs',
    coverStart: '#e4e4e4',
    coverEnd: '#d6d6d6',
  },
  {
    id: 'favorites-video-1',
    tab: 'favorites',
    type: 'video',
    title: 'Peppa Pig English',
    coverStart: '#e8e8e8',
    coverEnd: '#d9d9d9',
  },
  {
    id: 'favorites-video-2',
    tab: 'favorites',
    type: 'video',
    title: 'Alphablocks',
    coverStart: '#e5e5e5',
    coverEnd: '#d7d7d7',
  },
  {
    id: 'history-card-1',
    tab: 'history',
    type: 'card',
    title: 'TPR词卡 身体指令',
    coverStart: '#e8f4ff',
    coverEnd: '#d4e8ff',
  },
  {
    id: 'history-card-2',
    tab: 'history',
    type: 'card',
    title: 'TPR词卡 日常生活',
    coverStart: '#fff0df',
    coverEnd: '#ffe2c5',
  },
  {
    id: 'history-card-3',
    tab: 'history',
    type: 'card',
    title: '单词闪卡 26个英文字母',
    coverStart: '#e8f4ff',
    coverEnd: '#ffe2c5',
  },
  {
    id: 'favorites-card-1',
    tab: 'favorites',
    type: 'card',
    title: '动物主题词卡',
    coverStart: '#e8f5e9',
    coverEnd: '#c8e6c9',
  },
  {
    id: 'history-audio-1',
    tab: 'history',
    type: 'audio',
    title: 'Good Morning Song 晨间律动儿歌',
    date: '2026-05-02',
    coverStart: '#fff5d8',
    coverEnd: '#ffe3a3',
  },
  {
    id: 'history-audio-2',
    tab: 'history',
    type: 'audio',
    title: '日常英语听力',
    date: '2026-04-28',
    coverStart: '#e8f7ff',
    coverEnd: '#c7ebff',
  },
  {
    id: 'favorites-audio-1',
    tab: 'favorites',
    type: 'audio',
    title: '晚安英文儿歌',
    date: '2026-04-18',
    coverStart: '#fff0df',
    coverEnd: '#ffd6bc',
  },
  {
    id: 'history-word-1',
    tab: 'history',
    type: 'word',
    title: '26个英文字母',
    coverStart: '#e8f4ff',
    coverEnd: '#d4e8ff',
  },
  {
    id: 'history-word-2',
    tab: 'history',
    type: 'word',
    title: '基础单词闪卡',
    coverStart: '#f2e8ff',
    coverEnd: '#dfc9ff',
  },
  {
    id: 'favorites-word-1',
    tab: 'favorites',
    type: 'word',
    title: '动物单词收藏',
    coverStart: '#e8f5e9',
    coverEnd: '#c8e6c9',
  },
])

const getRouteTab = () => {
  const tab = route.query.tab

  if (tab === 'favorites') {
    return 'favorites'
  }

  return 'history'
}

const getRouteType = () => {
  const type = route.query.type

  if (typeOptions.some((option) => option.id === type)) {
    return type
  }

  return 'video'
}

const getHistoryRouteQuery = (tabId, typeId) => {
  return {
    tab: tabId,
    type: typeId,
  }
}

const activeTab = ref(getRouteTab())
const activeType = ref(getRouteType())
const isEditing = ref(false)
const selectedIds = ref([])
const currentAudioItemId = ref('')
const currentAudioSrc = ref('')
const currentTprItemId = ref('')
const audioPlayerRef = ref(null)
const parentPasswordModalRef = ref(null)
const historyShellRef = ref<HTMLElement | null>(null)
const tprVideoRefs = new Map()
let historyScrollContainer: HTMLElement | null = null
const activeNav = computed(() => activeTab.value === 'favorites' ? 'favorites' : 'history')
const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看历史', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]


const currentTabLabel = computed(() => {
  return tabs.find((tab) => tab.id === activeTab.value)?.label || '历史'
})

const visibleItems = computed(() => {
  if (activeTab.value === 'history' && isApiListView.value) {
    return historyVideoItems.value
  }

  if (activeTab.value === 'favorites' && isApiListView.value) {
    return favoriteVideoItems.value
  }

  return contentItems.value.filter((item) => item.tab === activeTab.value && item.type === activeType.value)
})

const currentAudioItem = computed(() => {
  return visibleItems.value.find((item) => item.id === currentAudioItemId.value) || null
})

const hasSelectedItems = computed(() => selectedIds.value.length > 0)
const dataTotalPages = computed(() => Math.ceil(dataTotal.value / queryParams.value.pageSize))
const hasNextVideoPage = computed(() => queryParams.value.pageNo < dataTotalPages.value)
const isVideoListView = computed(() => (activeTab.value === 'history' || activeTab.value === 'favorites') && activeType.value === 'video')
const isApiListView = computed(() => (activeTab.value === 'history' || activeTab.value === 'favorites') && ['video', 'audio', 'card', 'word'].includes(activeType.value))
const currentVideoList = computed(() => activeTab.value === 'favorites' ? favoritesList.value : historyList.value)
const showVideoSkeleton = computed(() => isApiListView.value && isLoading.value && currentVideoList.value.length === 0)
const isVideoPageLoading = computed(() => isApiListView.value && isLoading.value && currentVideoList.value.length > 0)

const selectTab = (tabId) => {
  activeTab.value = tabId
  selectedIds.value = []
  router.replace({
    path: '/history',
    query: getHistoryRouteQuery(tabId, activeType.value),
  })
}

const selectType = (typeId) => {
  activeType.value = typeId
  selectedIds.value = []
  resetVideoQuery()
  syncQueryContentType()
  router.replace({
    path: '/history',
    query: getHistoryRouteQuery(activeTab.value, typeId),
  })
  if (isApiListView.value) {
    loadVideoList()
  }
}

const toggleEditing = () => {
  isEditing.value = !isEditing.value

  if (!isEditing.value) {
    selectedIds.value = []
  }
}

const selectAllVisibleItems = () => {
  selectedIds.value = visibleItems.value.map((item) => item.id)
}

const finishEditingAfterDelete = () => {
  selectedIds.value = []
  isEditing.value = false
}

const deleteSelectedItems = () => {
  if (!hasSelectedItems.value) {
    return
  }
  const selectedSet = new Set(selectedIds.value)
  const isPlayingTprSelected = visibleItems.value.some((item) => {
    const playId = item.playId || item.contentId || item.id
    return selectedSet.has(item.id) && playId === currentTprItemId.value
  })

  if (activeTab.value === 'history' && isApiListView.value) {
    deleteHistory(selectedIds.value)
    const selectedCount = historyList.value.filter((item) => selectedSet.has(getHistoryVideoItemId(item))).length
    historyList.value = historyList.value.filter((item) => !selectedSet.has(getHistoryVideoItemId(item)))
    dataTotal.value = Math.max(0, dataTotal.value - selectedCount)
    if (isPlayingTprSelected) {
      pauseAllTprVideos()
      currentTprItemId.value = ''
    }
    finishEditingAfterDelete()
    return
  }

  if (activeTab.value === 'favorites' && isApiListView.value) {
    batchDeleteFavorites(selectedIds.value)
    const selectedCount = favoritesList.value.filter((item) => selectedSet.has(getFavoriteVideoItemId(item))).length
    favoritesList.value = favoritesList.value.filter((item) => !selectedSet.has(getFavoriteVideoItemId(item)))
    dataTotal.value = Math.max(0, dataTotal.value - selectedCount)
    if (isPlayingTprSelected) {
      pauseAllTprVideos()
      currentTprItemId.value = ''
    }
    finishEditingAfterDelete()
    return
  }

  contentItems.value = contentItems.value.filter((item) => !selectedSet.has(item.id))
  if (selectedSet.has(currentAudioItemId.value)) {
    currentAudioItemId.value = ''
  }
  if (isPlayingTprSelected) {
    pauseAllTprVideos()
    currentTprItemId.value = ''
  }
  finishEditingAfterDelete()
}

const handleGoBack = () => {
  router.push({
    path: '/',
    query: {
      tab: 'profile',
    },
  })
}

const openParentPasswordModal = () => {
  parentPasswordModalRef.value?.open()
}

const handleNavItemClick = (item) => {
  if (item.id === 'home') {
    router.push('/')
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites', type: activeType.value } : { tab: 'history', type: activeType.value }
    })
  }
}

const handleCardClick = (item) => {
  if (isEditing.value) {
    selectedIds.value = selectedIds.value.includes(item.id)
      ? selectedIds.value.filter((id) => id !== item.id)
      : [...selectedIds.value, item.id]
    return
  }

  if (item.type === 'video') {
    if (item.seriesId) {
      router.push({
        path: '/player',
        query: {
          seriesId: item.seriesId,
          seasonId: item.seasonId,
          episodesId: item.contentId,
        },
      })
      return
    }

    router.push({
      path: '/play',
      query: item.season && item.episode ? { season: item.season, episode: item.episode } : {},
    })
    return
  }

  if (item.type === 'card') {
    playTprItem(item)
    return
  }

  if (item.type === 'audio') {
    playAudioItem(item)
    return
  }

  if (item.type === 'word') {
    const seriesId = item.seriesId || item.contentId
    router.push({
      path: '/word',
      query: seriesId ? { seriesId } : {}
    })
  }
}

const playAudioItem = async (item) => {
  currentAudioItemId.value = item.id
  currentAudioSrc.value = item.audioUrl || ''
  pauseAllTprVideos()
  currentTprItemId.value = ''

  await Promise.resolve()

  if (!audioPlayerRef.value || !currentAudioSrc.value) {
    return
  }

  audioPlayerRef.value.currentTime = 0
  audioPlayerRef.value.play().catch(() => {})
}

const handleAudioEnded = () => {
  currentAudioItemId.value = ''
}

const setTprVideoRef = (cardId, el) => {
  const normalizedCardId = normalizeId(cardId)

  if (el instanceof HTMLVideoElement) {
    tprVideoRefs.set(normalizedCardId, el)
    return
  }

  tprVideoRefs.delete(normalizedCardId)
}

const playVideo = async (video, restart = false) => {
  if (!video) {
    return
  }

  if (restart) {
    video.currentTime = 0
  }

  try {
    await video.play()
  } catch {
    // Browser autoplay rules can block playback; the card state stays usable.
  }
}

const pauseAllTprVideos = (exceptId = null) => {
  const normalizedExceptId = normalizeId(exceptId)

  tprVideoRefs.forEach((video, cardId) => {
    if (!video || cardId === normalizedExceptId) {
      return
    }

    video.pause()
    video.currentTime = 0
  })
}

const getTprRequestBaseParams = () => {
  return route.query.stage ? { stage: route.query.stage } : {}
}

const mergeTprDetails = (list: TprVO[] = []) => {
  const nextMap = { ...tprDetailMap.value }

  list.forEach((item) => {
    const itemId = normalizeId(item.id)
    if (itemId) {
      nextMap[itemId] = item
    }
  })

  tprDetailMap.value = nextMap
}

const loadAllTprDetails = async () => {
  const cacheKey = normalizeId(route.query.stage || 'all')
  if (loadedTprDetailKeys.has(cacheKey)) {
    return
  }

  const baseParams = getTprRequestBaseParams()
  const categories = [
    '\u8eab\u4f53\u6307\u4ee4',
    '\u65e5\u5e38\u751f\u6d3b',
    '\u60c5\u7eea\u611f\u53d7'
  ]

  const results = await Promise.allSettled([
    getTprList(baseParams),
    ...categories.map((category) => getTprList({ ...baseParams, category }))
  ])

  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      mergeTprDetails(result.value || [])
    }
  })

  loadedTprDetailKeys.add(cacheKey)
}

const preloadTprDetailsForItems = async (list: ApiListItem[]) => {
  if (activeType.value !== 'card' || list.length === 0) {
    return
  }

  const hasMissingVideo = list.some((item) => !getApiItemVideoUrl(item))
  if (hasMissingVideo) {
    await loadAllTprDetails()
  }
}

const preloadApiDetailsForItems = async (list: ApiListItem[]) => {
  await preloadTprDetailsForItems(list)
}

const resolveTprPlayableItem = (item) => {
  const contentId = normalizeId(item.contentId || item.playId || item.id)
  const cachedTpr = contentId ? tprDetailMap.value[contentId] : null

  if (!cachedTpr) {
    return item
  }

  return {
    ...item,
    title: item.title || [cachedTpr.english, cachedTpr.chinese].filter(Boolean).join(' / ') || 'TPR',
    videoUrl: item.videoUrl || cachedTpr.videoUrl || '',
    coverUrl: item.coverUrl || cachedTpr.coverUrl || '',
  }
}

const playTprItem = async (item, restart = true) => {
  let playableItem = resolveTprPlayableItem(item)

  if (!playableItem.videoUrl) {
    await loadAllTprDetails()
    await nextTick()
    playableItem = resolveTprPlayableItem(item)
  }

  if (!playableItem.videoUrl) {
    return
  }

  const playId = normalizeId(playableItem.playId || playableItem.contentId || playableItem.id)
  let video = tprVideoRefs.get(playId)
  if (!video) {
    await nextTick()
    video = tprVideoRefs.get(playId)
  }

  if (!video) {
    return
  }

  if (video.getAttribute('src') !== playableItem.videoUrl) {
    video.src = playableItem.videoUrl
    video.load()
  }

  if (currentTprItemId.value === playId && !video.paused && !video.ended) {
    video.pause()
    return
  }

  currentTprItemId.value = playId
  currentAudioItemId.value = ''
  currentAudioSrc.value = ''
  pauseAllTprVideos(playId)

  await playVideo(video, restart)
}

const handleTprVideoElementClick = (item) => {
  const playId = normalizeId(item.playId || item.contentId || item.id)
  const video = tprVideoRefs.get(playId)
  if (!video) {
    return
  }

  if (currentTprItemId.value !== playId) {
    handleCardClick(item)
  }
}

const handleTprVideoPlay = (cardId) => {
  currentTprItemId.value = normalizeId(cardId)
  currentAudioItemId.value = ''
  currentAudioSrc.value = ''
  pauseAllTprVideos(currentTprItemId.value)
}

const handleTprVideoPause = (cardId) => {
  if (currentTprItemId.value === normalizeId(cardId)) {
    currentTprItemId.value = ''
  }
}

const handleTprVideoEnded = (cardId) => {
  if (currentTprItemId.value === normalizeId(cardId)) {
    currentTprItemId.value = ''
  }
}

const isPageNearBottom = () => {
  const scrollContainer = historyShellRef.value

  if (scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight + 1) {
    return scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 120
  }

  const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight
  const pageHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)

  return scrollTop + viewportHeight >= pageHeight - 120
}

const loadNextVideoPage = async () => {
  if (!isApiListView.value || isLoading.value || !hasNextVideoPage.value) {
    return
  }

  queryParams.value.pageNo += 1
  await loadVideoList(true)
}

const handlePageScroll = () => {
  if (isPageNearBottom()) {
    loadNextVideoPage()
  }
}

const resetVideoQuery = () => {
  queryParams.value.pageNo = 1
  dataTotal.value = 0
  historyList.value = []
  favoritesList.value = []
}

const loadVideoList = async (append = false) => {
  if (!isApiListView.value) {
    return
  }

  if (activeTab.value === 'favorites') {
    await getFavoriteList(append)
    return
  }

  await getHistoryList(append)
}

watch(
  () => [route.query.tab, route.query.type],
  () => {
    activeTab.value = getRouteTab()
    activeType.value = getRouteType()
    selectedIds.value = []
    resetVideoQuery()
    syncQueryContentType()
    if (isApiListView.value) {
      loadVideoList()
    }
  }
)

watch(activeType, () => {
  selectedIds.value = []
  if (activeType.value !== 'audio') {
    currentAudioItemId.value = ''
    currentAudioSrc.value = ''
  }
  if (activeType.value !== 'card') {
    pauseAllTprVideos()
    currentTprItemId.value = ''
  }
})

onMounted(async () => {
  syncQueryContentType()
  loadVideoList()
  historyScrollContainer = historyShellRef.value
  window.addEventListener('scroll', handlePageScroll, { passive: true })
  historyScrollContainer?.addEventListener('scroll', handlePageScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handlePageScroll)
  historyScrollContainer?.removeEventListener('scroll', handlePageScroll)
  historyScrollContainer = null
  pauseAllTprVideos()
})

const getFavoriteList = async (append = false) => {
  if (isLoading.value) {
    return
  }

  isLoading.value = true
  try {
    const data = await getFavoritesPage(queryParams.value)
    const list = data?.list || []
    await preloadApiDetailsForItems(list)
    favoritesList.value = append ? [...favoritesList.value, ...list] : list
    dataTotal.value = data?.total || 0
  } catch {
    if (append) {
      queryParams.value.pageNo = Math.max(1, queryParams.value.pageNo - 1)
    }
  } finally {
    isLoading.value = false
  }
}

const getHistoryList = async (append = false) => {
  if (isLoading.value) {
    return
  }

  isLoading.value = true
  try {
    const data = await getHistoryPage(queryParams.value)
    const list = data?.list || []
    await preloadApiDetailsForItems(list)
    historyList.value = append ? [...historyList.value, ...list] : list
    dataTotal.value = data?.total || 0
  } catch {
    if (append) {
      queryParams.value.pageNo = Math.max(1, queryParams.value.pageNo - 1)
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.history-page {
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  background: #fbf7f2;
  padding: 0;
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.history-page.editing {
  padding-bottom: 0;
}

.content-wrapper {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 100vh;
}

.sidebar {
  width: 264px;
  flex-shrink: 0;
  height: 100vh;
  overflow: hidden;
  background: #FFF9F5;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 2;
}

.nav-card {
  height: 100%;
  min-height: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 20px;
  border-bottom: 1px solid rgba(255, 138, 61, 0.15);
}

.nav-logo .logo-icon {
  width: 62px;
  height: 62px;
  background: #fff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(140, 104, 68, 0.12);
}

.nav-logo .logo2-icon {
  display: block;
  width: 52px;
  height: 52px;
  object-fit: contain;
}

.brand-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.nav-logo .brand-name {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #2D3436;
  letter-spacing: 1px;
  line-height: 1.05;
}

.brand-subtitle {
  color: #a78b76;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.2px;
  line-height: 1;
}

.nav-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: clamp(12px, 2vh, 20px);
  padding: clamp(12px, 2vh, 16px) 12px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 14px;
  color: #666;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  text-decoration: none;
}

.nav-item:hover {
  background: rgba(255, 138, 61, 0.08);
  color: #FF8A3D;
}

.nav-item.active {
  background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%);
  color: #FF6B35;
  font-weight: 700;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.2);
}

.nav-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.nav-menu-visual {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  margin-top: auto;
  border-radius: 0;
  overflow: hidden;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.nav-menu-image {
  display: block;
  flex-shrink: 0;
  width: 100%;
  min-width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center bottom;
}

.bg-decoration {
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
}

.bg-decoration::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 18% 18%, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0) 28%),
    linear-gradient(135deg, #fbf8f4 0%, #f7f1ea 52%, #f1e9df 100%);
}

.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.48;
}

.orb-1 {
  width: 430px;
  height: 430px;
  top: -150px;
  right: -130px;
  background: linear-gradient(135deg, #ffe6d7, #ffd2bd);
}

.orb-2 {
  width: 340px;
  height: 340px;
  bottom: -90px;
  left: -110px;
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
}

.orb-3 {
  width: 300px;
  height: 300px;
  top: 38%;
  left: 52%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  opacity: 0.28;
}

.history-shell {
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 1;
  width: min(100% - 28px, 460px);
  margin: 0 auto;
  padding: 24px 16px 48px;
  animation: history-fade-up 0.42s ease both;
}

.history-page.editing .history-shell {
  padding-bottom: 132px;
}

@media (min-width: 769px) {
  .history-shell {
    margin-left: 32px;
    margin-right: 32px;
  }
}

.history-tabs {
  display: grid;
  grid-template-columns: repeat(2, 84px);
  justify-content: start;
  align-items: center;
  column-gap: 34px;
  padding: 0 6px;
}

@keyframes history-fade-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mobile-back-btn {
  display: none;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  align-items: center;
  justify-content: center;
}

.history-tab {
  position: relative;
  height: 36px;
  border: none;
  background: transparent;
  color: #888991;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  transition: color 0.22s ease, transform 0.22s ease;
}

.history-tab:hover {
  color: #42454b;
}

.history-tab.active {
  color: #1f2329;
  transform: translateY(-1px);
}

.history-tab.active::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 34px;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, #ffb16f 0%, #ff7b34 100%);
  box-shadow: 0 6px 14px rgba(255, 123, 52, 0.28);
  transform: translateX(-50%);
}

.history-toolbar {
  margin-top: 26px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
  padding: 4px 0;
}

.type-tabs {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex-wrap: wrap;
}

.type-tab {
  appearance: none;
  -webkit-appearance: none;
  min-width: 76px;
  min-height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: #666;
  font-size: 16px;
  font-weight: 600;
  padding: 10px 22px;
  cursor: pointer;
  transition: all 0.25s ease;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: none;
  user-select: none;
}

.type-tab.active {
  background: #ff8c42;
  border-color: #ff8c42;
  color: #fff;
  font-weight: 700;
  box-shadow: 0 8px 30px rgba(255, 140, 66, 0.35);
}

.type-tab:hover {
  background: rgba(255, 255, 255, 0.8);
  transform: translateY(-1px);
}

.type-tab.active:hover {
  background: #ff8c42;
}

.edit-btn {
  border: 1px solid transparent;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.42);
  color: #5f6065;
  font-size: 14px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  min-height: 32px;
  padding: 0 10px;
  flex: 0 0 auto;
  transition: color 0.22s ease, background 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;
}

.edit-btn:hover {
  background: rgba(255, 255, 255, 0.78);
  border-color: rgba(255, 177, 111, 0.34);
  color: #ff7b34;
}

.edit-btn svg {
  font-size: 16px;
}

.edit-btn.active {
  color: #ff7b34;
  background: rgba(255, 140, 66, 0.1);
  border-color: rgba(255, 140, 66, 0.28);
  box-shadow: 0 8px 24px rgba(255, 140, 66, 0.14);
}

.content-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px 14px;
  align-items: start;
}

.content-grid.type-card {
  grid-template-columns: repeat(auto-fill, minmax(148px, 180px));
  justify-content: start;
  gap: 18px 14px;
}

.content-card {
  min-width: 0;
  cursor: pointer;
  border-radius: 8px;
  transition: transform 0.25s ease, filter 0.25s ease;
}

.skeleton-card {
  cursor: default;
  pointer-events: none;
}

.skeleton-card :deep(.el-skeleton) {
  width: 100%;
}

.skeleton-cover {
  width: 100%;
  aspect-ratio: 1.73 / 1;
  height: auto;
  border-radius: 8px;
}

.skeleton-title {
  width: 76%;
  height: 18px;
  margin: 12px 5px 0;
  border-radius: 999px;
}

.favorite-loading-state {
  grid-column: 1 / -1;
  width: min(240px, 70%);
  justify-self: center;
  padding: 6px 0 2px;
}

.favorite-loading-line {
  height: 14px;
  border-radius: 999px;
}

.content-card-audio {
  position: relative;
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 18px;
  min-height: 64px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.82);
  box-shadow: 0 10px 24px rgba(255, 140, 66, 0.06);
}

.content-card-audio.playing {
  border-color: rgba(255, 140, 66, 0.55);
  box-shadow:
    0 18px 42px rgba(255, 140, 66, 0.18),
    0 0 0 1px rgba(255, 140, 66, 0.16);
}

.content-card-audio.playing .audio-history-icon {
  background: #ff8c42;
  color: #ffffff;
}

.audio-history-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: #fff4ec;
  color: #ff8c42;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 23px;
  flex: 0 0 auto;
}

.audio-history-copy {
  min-width: 0;
}

.audio-history-title {
  margin: 0;
  color: #30363a;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audio-history-date {
  margin: 8px 0 0;
  color: #a28d7c;
  font-size: 14px;
  font-weight: 800;
  line-height: 1;
}

.audio-select-mark {
  top: 2px;
  right: 2px;
}

.content-card-card {
  position: relative;
}

.tpr-history-surface {
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  display: block;
  cursor: pointer;
}

.tpr-history-video-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(145deg, var(--cover-start), var(--cover-end));
  border: 1px solid rgba(255, 255, 255, 0.72);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.38),
    0 10px 24px rgba(78, 64, 52, 0.08);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.tpr-history-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.46);
}

.tpr-history-video::-webkit-media-controls,
.tpr-history-video::-webkit-media-controls-enclosure {
  display: none !important;
}

.tpr-history-video-status {
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 18px;
  pointer-events: none;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
}

.content-card-card:hover .tpr-history-video-wrap,
.content-card-card.playing .tpr-history-video-wrap {
  transform: translateY(-4px) scale(1.01);
  border-color: rgba(255, 177, 111, 0.42);
  box-shadow:
    0 20px 44px rgba(255, 140, 66, 0.22),
    0 8px 22px rgba(255, 140, 66, 0.14);
}

.history-audio-player-dock {
  position: fixed;
  left: 50%;
  bottom: 22px;
  z-index: 80;
  width: min(calc(100% - 32px), 620px);
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.82);
  box-shadow: 0 18px 48px rgba(102, 70, 43, 0.18);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  transform: translateX(-50%);
}

.history-audio-player-copy {
  min-width: 0;
  margin-bottom: 10px;
}

.history-audio-player-label {
  display: block;
  color: #a28d7c;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
}

.history-audio-player-title {
  display: block;
  margin-top: 6px;
  color: #30363a;
  font-size: 15px;
  font-weight: 900;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-audio-player {
  display: block;
  width: 100%;
  height: 38px;
}

.player-dock-enter-active,
.player-dock-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.player-dock-enter-from,
.player-dock-leave-to {
  opacity: 0;
  transform: translate(-50%, 12px);
}

.cover-box {
  position: relative;
  width: 100%;
  aspect-ratio: 1.73 / 1;
  border-radius: 8px;
  background: linear-gradient(145deg, var(--cover-start), var(--cover-end));
  border: 1px solid rgba(255, 255, 255, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.64),
    0 10px 24px rgba(78, 64, 52, 0.05);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.cover-box::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 28% 20%, rgba(255, 255, 255, 0.72) 0%, rgba(255, 255, 255, 0) 34%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 62%);
  pointer-events: none;
}

.cover-box.has-cover {
  background: #fff;
}

.cover-box.has-cover::before {
  content: none;
}

.cover-box::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(110deg, transparent 0%, rgba(255, 255, 255, 0.44) 42%, transparent 68%);
  opacity: 0;
  transform: translateX(-40%);
  transition: opacity 0.28s ease, transform 0.5s ease;
  pointer-events: none;
}

.content-card:hover .cover-box {
  transform: translateY(-4px) scale(1.01);
  border-color: rgba(255, 177, 111, 0.42);
  box-shadow:
    0 20px 44px rgba(255, 140, 66, 0.22),
    0 8px 22px rgba(255, 140, 66, 0.14);
}

.content-card:hover .cover-box::after {
  opacity: 1;
  transform: translateX(34%);
}

.content-card:focus-within .cover-box {
  box-shadow:
    0 0 0 3px rgba(255, 140, 66, 0.2),
    0 16px 36px rgba(255, 140, 66, 0.18);
}

.cover-icon {
  position: relative;
  z-index: 1;
  width: 32px;
  height: 32px;
  padding: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.52);
  color: #8ca3b2;
  font-size: 15px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.74);
}

.cover-image {
  position: absolute;
  inset: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.select-mark {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.95);
  background: rgba(255, 255, 255, 0.56);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
}

.content-card.selected .select-mark {
  border-color: #ff8c42;
  background: #ff8c42;
}

.content-card.selected .cover-box {
  box-shadow:
    0 20px 60px rgba(255, 140, 66, 0.3),
    0 0 0 2px #ff8c42;
}

.content-title {
  margin: 12px 5px 0;
  color: #44464b;
  font-size: 14px;
  font-weight: 800;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-state {
  grid-column: 1 / -1;
  min-height: 220px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.84);
  color: #9a9a9a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 15px;
  font-weight: 800;
  box-shadow: 0 14px 34px rgba(95, 80, 68, 0.06);
}

.empty-state svg {
  color: #c7c7c7;
  font-size: 28px;
}

.batch-actions {
  position: fixed;
  left: calc(280px + (100vw - 280px) / 2);
  bottom: calc(env(safe-area-inset-bottom, 0px) + 16px);
  z-index: 40;
  width: min(calc(100% - 28px), 460px);
  transform: translateX(-50%);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 10px;
  border-radius: 20px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.84), rgba(255, 248, 241, 0.72));
  border: 1px solid rgba(255, 255, 255, 0.9);
  box-shadow:
    0 18px 46px rgba(95, 80, 68, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.batch-btn {
  min-height: 46px;
  border: none;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease, color 0.22s ease;
}

.batch-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.select-all-btn {
  background: rgba(255, 255, 255, 0.76);
  color: #4f5258;
  border: 1px solid rgba(227, 217, 205, 0.9);
}

.select-all-btn:hover {
  color: #ff7b34;
  box-shadow: 0 10px 24px rgba(255, 140, 66, 0.12);
}

.delete-btn {
  background: #e3e0dc;
  color: #aaa29b;
  cursor: not-allowed;
}

.delete-btn.active {
  background: linear-gradient(135deg, #ff9a3e 0%, #ff7534 100%);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(255, 140, 66, 0.32);
}

.delete-btn.active:hover {
  box-shadow: 0 16px 36px rgba(255, 140, 66, 0.4);
}

.batch-bar-enter-active,
.batch-bar-leave-active {
  transition: opacity 0.24s ease;
}

.batch-bar-enter-active.batch-actions,
.batch-bar-leave-active.batch-actions {
  transition: transform 0.24s ease;
}

.batch-bar-enter-from,
.batch-bar-leave-to {
  opacity: 0;
}

.batch-bar-enter-from.batch-actions,
.batch-bar-leave-to.batch-actions {
  transform: translate(-50%, 20px);
}

@media (min-width: 768px) {
  .history-page {
    height: 100vh;
    overflow: hidden;
  }

  .content-wrapper {
    height: 100vh;
    min-height: 0;
    overflow: hidden;
  }

  .history-shell {
    height: 100vh;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
    padding-top: 24px;
    padding-left: 20px;
    padding-right: 20px;
    padding-bottom: 64px;
    scrollbar-gutter: stable;
  }

  .history-page.editing .history-shell {
    padding-bottom: 140px;
  }

  .history-shell {
    width: min(100% - 56px, 920px);
  }

  .history-tabs {
    justify-content: start;
    column-gap: 46px;
    padding-left: 4px;
  }

  .history-toolbar {
    margin-top: 28px;
    padding: 14px 16px;
    border-radius: 8px;
    background:
      linear-gradient(135deg, rgba(255, 255, 255, 0.76), rgba(255, 249, 242, 0.62));
    border: 1px solid rgba(255, 255, 255, 0.86);
    box-shadow:
      0 14px 34px rgba(95, 80, 68, 0.07),
      inset 0 1px 0 rgba(255, 255, 255, 0.76);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
  }

  .content-grid {
    margin-top: 22px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px 18px;
  }

  .content-grid.type-card {
    grid-template-columns: repeat(auto-fill, minmax(156px, 190px));
    gap: 22px 18px;
  }

  .type-tab {
    min-width: 86px;
  }

  .content-title {
    font-size: 15px;
  }

  .batch-actions {
    width: min(calc(100% - 56px), 520px);
  }
}

@media (min-width: 1120px) {
  .history-shell {
    padding-top: 28px;
    padding-left: 24px;
    padding-right: 24px;
  }

  .history-shell {
    width: min(100% - 80px, 1180px);
  }

  .history-tabs {
    grid-template-columns: repeat(2, 96px);
  }

  .history-tab {
    font-size: 16px;
  }

  .content-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 28px 22px;
  }

  .content-card {
    padding: 10px;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.74) 0%, rgba(255, 250, 245, 0.56) 100%);
    border: 1px solid rgba(255, 255, 255, 0.84);
    box-shadow: 0 10px 24px rgba(255, 140, 66, 0.06);
    transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
  }

  .content-card:hover {
    transform: translateY(-8px) scale(1.01);
    border-color: rgba(255, 140, 66, 0.55);
    box-shadow:
      0 24px 54px rgba(255, 140, 66, 0.22),
      0 8px 24px rgba(255, 140, 66, 0.12);
  }

  .content-card:hover .cover-box {
    transform: none;
    box-shadow: none;
  }

  .content-card.selected {
    border-color: #ff8c42;
    box-shadow:
      0 20px 60px rgba(255, 140, 66, 0.3),
      0 0 0 1px rgba(255, 140, 66, 0.28);
  }

  .content-card.selected .cover-box {
    box-shadow: none;
  }

  .content-title {
    margin-top: 12px;
    margin-bottom: 2px;
  }

  .empty-state {
    min-height: 300px;
  }
}

@media (max-width: 1200px) and (min-width: 768px) {
  .sidebar {
    width: 224px;
  }

  .nav-logo {
    padding: 20px 20px;
    gap: 16px;
  }

  .nav-logo .logo-icon {
    width: 62px;
    height: 62px;
    font-size: 20px;
  }

  .nav-logo .logo2-icon {
    width: 52px;
    height: 52px;
  }

  .nav-logo .brand-name {
    font-size: 24px;
  }

  .nav-logo .brand-subtitle {
    font-size: 12px;
  }

  .nav-item {
    padding: 16px;
  }

  .batch-actions {
    left: calc(224px + (100vw - 224px) / 2);
  }
}

@media (max-width: 992px) and (min-width: 768px) {
  .sidebar {
    width: 208px;
  }

  .batch-actions {
    left: calc(208px + (100vw - 208px) / 2);
  }
}

@media (max-width: 767px), (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
  .history-page {
    height: auto;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .content-wrapper {
    display: block;
    min-height: 100vh;
    height: auto;
    overflow: visible;
  }

  .sidebar {
    display: none;
  }

  .history-page.editing .history-shell {
    padding-bottom: 118px;
  }

  .batch-actions {
    left: 50%;
  }

  .history-shell {
    height: auto;
    overflow: visible;
    padding-top: calc(env(safe-area-inset-top, 0px) + 6px);
  }

  .mobile-back-btn {
    box-shadow:
      0 8px 24px rgba(0, 0, 0, 0.06),
      inset 0 1px 0 rgba(255, 255, 255, 0.78);
  }

  .history-tabs {
    grid-template-columns: 44px repeat(2, 72px);
    align-items: center;
    column-gap: 18px;
    padding-left: 0;
  }

  .mobile-back-btn {
    display: inline-flex;
  }

  .history-toolbar {
    margin-top: 22px;
    gap: 12px;
    align-items: center;
  }

  .type-tabs {
    gap: 10px;
    flex: 1;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 2px 2px 8px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .type-tabs::-webkit-scrollbar {
    display: none;
  }

  .type-tab {
    min-width: 70px;
    min-height: 34px;
    padding: 7px 16px;
    font-size: 14px;
  }

  .edit-btn span {
    max-width: 42px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

@media (max-width: 390px) {
  .history-shell {
    width: calc(100% - 22px);
  }

  .history-tabs {
    grid-template-columns: 40px repeat(2, 64px);
    column-gap: 14px;
  }

  .mobile-back-btn {
    width: 40px;
    height: 40px;
  }

  .type-tabs {
    gap: 8px;
  }

  .type-tab {
    min-width: 62px;
    min-height: 32px;
    padding: 6px 13px;
    font-size: 13px;
  }

  .edit-btn {
    font-size: 13px;
  }

  .content-grid {
    gap: 18px 12px;
  }

  .content-grid.type-card {
    grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
    gap: 16px 12px;
  }

  .content-title {
    margin-left: 4px;
    margin-right: 4px;
    font-size: 13px;
  }
}
@media (min-width: 769px) {
  .nav-logo { padding: 20px 20px; gap: 16px; }
  .nav-logo .logo-icon { width: 62px; height: 62px; }
  .nav-logo .logo2-icon { width: 52px; height: 52px; }
  .nav-logo .brand-name { font-size: 24px; letter-spacing: 1px; line-height: 1.05; }
  .nav-logo .brand-subtitle { font-size: 12px; letter-spacing: 1.2px; line-height: 1; }
  .nav-item { padding: clamp(12px, 1.8vh, 18px) 18px; }
}

@media (min-width: 993px) and (max-width: 1200px) {
  .sidebar { width: 224px; }
}

@media (min-width: 769px) and (max-width: 992px) {
  .sidebar { width: 208px; }
}
</style>
